import { useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articleList } from '@/mocks/article';
import { getArticleSchemas } from '@/mocks/schema/article';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const articleId = Number(id) || 1;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const article = useMemo(() => {
    return articleList.find((a) => a.id === articleId) || articleList[0];
  }, [articleId]);

  const relatedArticles = useMemo(() => {
    return articleList.filter((a) => a.id !== articleId).slice(0, 3);
  }, [articleId]);

  const schemas = useMemo(() => getArticleSchemas(article), [article]);

  return (
    <>
      <title>{article.title} | 2pg·com 攻略资讯</title>
      <meta name="description" content={article.excerpt} />
      <link rel="canonical" href={`https://2pg.com/article/${article.id}`} />
      <SchemaOrg schemas={schemas} />

      <Navbar />
      <main className="pt-16">
        <section className="relative h-[360px] md:h-[420px] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 pb-10">
            <nav className="text-sm mb-3" aria-label="面包屑导航">
              <ol className="flex items-center gap-2 text-white/70">
                <li itemScope itemType="https://schema.org/ListItem">
                  <Link itemProp="item" to="/" className="hover:text-white transition-colors cursor-pointer">
                    <span itemProp="name">首页</span>
                  </Link>
                  <meta itemProp="position" content="1" />
                </li>
                <li className="text-white/40">/</li>
                <li itemScope itemType="https://schema.org/ListItem">
                  <Link itemProp="item" to="/articles" className="hover:text-white transition-colors cursor-pointer">
                    <span itemProp="name">攻略资讯</span>
                  </Link>
                  <meta itemProp="position" content="2" />
                </li>
                <li className="text-white/40">/</li>
                <li itemScope itemType="https://schema.org/ListItem" className="text-white">
                  <span itemProp="name">{article.title.slice(0, 20)}...</span>
                  <meta itemProp="position" content="3" />
                </li>
              </ol>
            </nav>
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-accent-500/80 text-white">{article.category}</span>
                <span className="text-sm text-white/70">{article.date}</span>
                <span className="text-sm text-white/70">· {article.readTime}阅读</span>
              </div>
              <h1 className="text-xl md:text-3xl font-bold font-heading text-white mb-2 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm text-white/80">作者：{article.author} · {article.views}次阅读</p>
            </div>
          </div>
        </section>

        <section className="py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">
              <article className="flex-1 max-w-3xl">
                {article.content.map((block, idx) => {
                  if (block.type === 'heading') {
                    return (
                      <h2 key={idx} className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mt-10 mb-4">
                        {block.text}
                      </h2>
                    );
                  }
                  return (
                    <p key={idx} className="text-base text-foreground-700 leading-relaxed mb-5">
                      {block.text}
                    </p>
                  );
                })}
              </article>

              <aside className="w-full lg:w-80 flex-shrink-0">
                <div className="bg-background-100 rounded-2xl p-6">
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-5">相关文章</h3>
                  <div className="flex flex-col gap-5">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="group flex gap-3 cursor-pointer"
                      >
                        <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={ra.imageUrl}
                            alt={ra.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors">
                            {ra.title}
                          </h4>
                          <span className="text-xs text-foreground-500">{ra.date}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}